import { forwardRef, memo, useCallback, useMemo } from 'react';
import { type DateTime } from 'luxon';
import { useIntl } from 'react-intl';
// Material UI imports
import {
  DatePicker as MuiDatePicker,
  type DatePickerProps as MuiDatePickerProps,
  type DatePickerSlotProps
} from '@mui/x-date-pickers/DatePicker';
// SCSS imports
import { root } from '../styles/modules/Lookup.module.scss';
import { openPickerButton } from './DatePicker.module.scss';

const openPickerButtonProps = { className: openPickerButton };

type DatePickerProps = {
  readonly value?: DateTime | null;
  readonly onChange?: (date: DateTime) => void;
  readonly error?: boolean;
  readonly helperText?: string;
  readonly placeholder?: string;
  readonly disabled?: boolean | null;
  readonly disablePast?: boolean;
  readonly disableFuture?: boolean;
  readonly minDate?: DateTime | null;
  readonly maxDate?: DateTime | null;
  readonly sx?: MuiDatePickerProps<boolean>['sx'];
}

const DatePicker = forwardRef<HTMLDivElement, DatePickerProps>(({
  value = null,
  onChange,
  error,
  helperText,
  placeholder,
  disabled = false,
  disablePast,
  disableFuture,
  minDate,
  maxDate,
  sx
}, ref) => {
  const { formatMessage } = useIntl();

  const handleChange = useCallback((date: unknown) => {
    if (onChange) onChange(date as DateTime);
  }, [onChange]);

  const slotProps = useMemo(() => ({
    textField: {
      size: 'small',
      error,
      helperText,
      variant: 'outlined',
      // TODO: remove temporary workaround for screen reader accessibility:
      inputProps: { type: undefined, placeholder: undefined }
    },
    openPickerButton: openPickerButtonProps
  } as DatePickerSlotProps<boolean>), [error, helperText]);

  return (
    <MuiDatePicker
        ref={ref}
        sx={sx}
        label={placeholder}
        slotProps={slotProps}
        value={value}
        format={formatMessage({ id: 'date_picker.date_format' })}
        onChange={handleChange}
        disabled={disabled ? true : undefined}
        disablePast={disablePast}
        disableFuture={disableFuture}
        minDate={minDate || undefined}
        maxDate={maxDate || undefined}
        className={root}
    />
  );
});

DatePicker.displayName = 'DatePicker';

export default memo(DatePicker);
